import React from "react";
import { Box, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { tokens, fmtNumber } from "../../shared/clinical-design";

// Colores sobrios por tier (semántica de producto, no decoración).
const TIER_COLORS = {
  Bronze: tokens.warn,
  Silver: tokens.muted,
  Gold: "#8A6D1A",
  Platinum: tokens.info,
  Catastrophic: tokens.crit,
  Other: tokens.lineStrong,
};

/**
 * Payer / plan mix: barra segmentada por tier + tabla densa de planes.
 *
 * @param {Object} planMix - { plans: [{plan, members, pct}], tiers: [{tier, members, pct}] }
 * @param {boolean} [loading]
 */
export default function PlanMixPanel({ planMix, loading = false }) {
  if (loading) return <Skeleton variant="rectangular" height={250} />;

  const plans = planMix?.plans || [];
  const tiers = planMix?.tiers || [];

  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: tokens.ink3,
          mb: 0.75,
        }}
      >
        Metal tier distribution
      </Typography>
      <Stack direction="row" sx={{ height: 14, borderRadius: "3px", overflow: "hidden", mb: 0.75 }}>
        {tiers.map((t) => (
          <Tooltip key={t.tier} title={`${t.tier}: ${fmtNumber(t.members)} (${t.pct.toFixed(1)}%)`} arrow>
            <Box sx={{ width: `${t.pct}%`, bgcolor: TIER_COLORS[t.tier] || tokens.muted, minWidth: 3 }} />
          </Tooltip>
        ))}
      </Stack>
      <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", rowGap: 0.5, mb: 1.5 }}>
        {tiers.map((t) => (
          <Stack key={t.tier} direction="row" alignItems="center" spacing={0.6}>
            <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: TIER_COLORS[t.tier] || tokens.muted }} />
            <Typography sx={{ fontSize: 11.5, color: tokens.ink2 }}>
              {t.tier} {t.pct.toFixed(1)}%
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: tokens.ink3,
          mb: 0.5,
        }}
      >
        Plans by enrollment (new members YTD)
      </Typography>
      <Box>
        {plans.map((p, i) => (
          <Stack
            key={p.plan}
            direction="row"
            alignItems="center"
            sx={{
              py: 0.55,
              px: 1,
              bgcolor: i % 2 === 1 ? tokens.surface2 : "transparent",
              borderBottom: `1px solid ${tokens.line}`,
            }}
          >
            <Typography sx={{ fontSize: 12.5, color: tokens.ink, flex: 1, whiteSpace: "nowrap" }}>
              {p.plan}
            </Typography>
            <Typography
              sx={{
                fontFamily: tokens.fontMono,
                fontVariantNumeric: "tabular-nums",
                fontSize: 12.5,
                color: tokens.ink,
                width: 56,
                textAlign: "right",
              }}
            >
              {fmtNumber(p.members)}
            </Typography>
            <Typography
              sx={{
                fontFamily: tokens.fontMono,
                fontVariantNumeric: "tabular-nums",
                fontSize: 12,
                color: tokens.ink3,
                width: 56,
                textAlign: "right",
              }}
            >
              {p.pct.toFixed(1)}%
            </Typography>
          </Stack>
        ))}
      </Box>
    </Box>
  );
}
